import type { URLSearchParamsInit } from 'react-router-dom';

export const PRESETS = ['today', 'yesterday', '7d', '30d', 'thisMonth', 'lastMonth'] as const;
export type Preset = (typeof PRESETS)[number];

export const MAX_DAYS = 366;

/** The range on screen: calendar days (YYYY-MM-DD) in the venue's time zone, both inclusive. */
export interface DateRange {
    kind: Preset | 'custom';
    from: string;
    to: string;
}

export type CustomError = 'missing' | 'order' | 'future' | 'tooLong';

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const utc = (day: string): number => Date.parse(`${day}T00:00:00Z`);
const iso = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

export const addDays = (day: string, n: number): string => iso(utc(day) + n * 86_400_000);
export const dayCount = (from: string, to: string): number => Math.round((utc(to) - utc(from)) / 86_400_000) + 1;

/** Today's calendar day in the given IANA zone. */
export const todayIn = (timeZone: string, now = new Date()): string =>
    new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now);

const monthStart = (day: string): string => `${day.slice(0, 7)}-01`;

export const validateCustom = (from: string, to: string, today: string): CustomError | null => {
    if (!DAY.test(from) || !DAY.test(to) || Number.isNaN(utc(from)) || Number.isNaN(utc(to))) return 'missing';
    if (from > to) return 'order';
    if (to > today) return 'future';
    if (dayCount(from, to) > MAX_DAYS) return 'tooLong';
    return null;
};

export const presetRange = (preset: Preset, today: string): DateRange => {
    const first = monthStart(today);
    const bounds: Record<Preset, [string, string]> = {
        today: [today, today],
        yesterday: [addDays(today, -1), addDays(today, -1)],
        '7d': [addDays(today, -6), today],
        '30d': [addDays(today, -29), today],
        thisMonth: [first, today],
        lastMonth: [monthStart(addDays(first, -1)), addDays(first, -1)],
    };
    const [from, to] = bounds[preset];
    return { kind: preset, from, to };
};

/** `?range=7d` or `?from=&to=`. Anything unknown or invalid falls back to today. */
export const readRange = (params: URLSearchParams, today: string): DateRange => {
    const preset = PRESETS.find((p) => p === params.get('range'));
    if (preset) return presetRange(preset, today);
    const from = params.get('from') ?? '';
    const to = params.get('to') ?? '';
    return validateCustom(from, to, today) === null ? { kind: 'custom', from, to } : presetRange('today', today);
};

/** Query for a choice; today is the default and leaves the URL clean. */
export const rangeQuery = (preset: Preset | { from: string; to: string }): URLSearchParamsInit =>
    typeof preset === 'string' ? (preset === 'today' ? {} : { range: preset }) : { from: preset.from, to: preset.to };
